/**
 * BookDemoModal — lightweight demo-request modal used by every
 * "Book a demo" CTA on the /trust marketing page.
 *
 * Deliberately minimal: name + work email + company + preferred time,
 * with an optional note. POSTs to /api/marketing/demo-request and
 * shows an inline success state (no toast, no redirect) so visitors
 * stay on the page.
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  'w-full h-11 px-3.5 rounded-lg border border-[#E2E8F0] dark:border-white/10 bg-white dark:bg-[#0F172A]/40 text-[14px] text-[#0A0F1C] dark:text-[#F5F5F5] placeholder:text-[#94A3B8] dark:placeholder:text-white/40 focus:outline-none focus:border-[#0F62FE] focus:ring-2 focus:ring-[#0F62FE]/20 transition-colors';

const labelCls =
  'block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#64748B] dark:text-white/60 mb-1.5';

const BookDemoModal = ({ open, onClose, source = 'trust_marketing_page' }) => {
  const [form, setForm] = useState({
    name: '',
    work_email: '',
    company: '',
    preferred_time: '',
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const firstFieldRef = useRef(null);

  // Reset & focus when opened
  useEffect(() => {
    if (open) {
      setForm({ name: '', work_email: '', company: '', preferred_time: '', note: '' });
      setError('');
      setDone(false);
      setSubmitting(false);
      // Focus after animation
      const t = setTimeout(() => {
        if (firstFieldRef.current) firstFieldRef.current.focus();
      }, 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Escape key closes modal
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    // lock body scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.work_email.trim() || !form.company.trim()) {
      setError('Please fill in your name, work email, and company.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/marketing/demo-request`, {
        ...form,
        source,
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI/Pydantic validation errors — surface the first field problem.
        const first = detail[0];
        const loc = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : '';
        const field = String(loc || '').replace(/_/g, ' ');
        msg = field
          ? `Please check the ${field} field — ${first?.msg || 'invalid value'}.`
          : first?.msg || 'Please check your details and try again.';
      } else {
        msg = 'Something went wrong. Please try again or email hello@teamnova.ai.';
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6 sm:py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="book-demo-title"
      data-testid="trust-marketing-book-demo-modal"
    >
      {/* Overlay */}
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#0A0F1C]/70 dark:bg-black/80 backdrop-blur-sm"
        data-testid="trust-marketing-book-demo-overlay"
      />
      {/* Panel */}
      <div
        className="relative w-full max-w-[520px] rounded-2xl bg-white dark:bg-[#0B1220] border border-[#E2E8F0] dark:border-white/10 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.4)] overflow-hidden"
      >
        <button
          type="button"
          aria-label="Close book a demo dialog"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full inline-flex items-center justify-center text-[#64748B] hover:text-[#0A0F1C] dark:text-white/60 dark:hover:text-white hover:bg-[#F1F5F9] dark:hover:bg-white/[0.06] transition-colors"
          data-testid="trust-marketing-book-demo-close"
        >
          <X className="w-4 h-4" />
        </button>

        {!done ? (
          <form onSubmit={submit} className="p-6 sm:p-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0F62FE]">
              Book a demo
            </div>
            <h3
              id="book-demo-title"
              className="mt-2 text-[22px] sm:text-[24px] font-bold tracking-tight text-[#0A0F1C] dark:text-[#F5F5F5] leading-tight"
            >
              See Nova Trust Center in your workflow.
            </h3>
            <p className="mt-1.5 text-[13.5px] text-[#475569] dark:text-white/70 leading-relaxed">
              30-minute walkthrough with a product specialist. No sales script — bring your
              hardest security-review questions.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4">
              <div>
                <label className={labelCls} htmlFor="bd-name">Full name</label>
                <input
                  id="bd-name"
                  ref={firstFieldRef}
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={update('name')}
                  className={inputCls}
                  placeholder="Jane Smith"
                  data-testid="book-demo-input-name"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="bd-email">Work email</label>
                  <input
                    id="bd-email"
                    type="email"
                    autoComplete="email"
                    value={form.work_email}
                    onChange={update('work_email')}
                    className={inputCls}
                    placeholder="jane@company.com"
                    data-testid="book-demo-input-email"
                    required
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bd-company">Company</label>
                  <input
                    id="bd-company"
                    type="text"
                    autoComplete="organization"
                    value={form.company}
                    onChange={update('company')}
                    className={inputCls}
                    placeholder="Acme Corp"
                    data-testid="book-demo-input-company"
                    required
                  />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="bd-time">Preferred time (optional)</label>
                <input
                  id="bd-time"
                  type="text"
                  value={form.preferred_time}
                  onChange={update('preferred_time')}
                  className={inputCls}
                  placeholder="Weekday mornings PT"
                  data-testid="book-demo-input-time"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="bd-note">Anything specific? (optional)</label>
                <textarea
                  id="bd-note"
                  value={form.note}
                  onChange={update('note')}
                  className={`${inputCls} h-24 py-2.5 resize-none`}
                  placeholder="e.g. we need AI governance published for a specific customer review"
                  data-testid="book-demo-input-note"
                />
              </div>
            </div>

            {error && (
              <div
                className="mt-4 text-[13px] text-[#B91C1C] dark:text-[#FCA5A5]"
                role="alert"
                data-testid="book-demo-error"
              >
                {error}
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-4 rounded-full text-[14px] font-semibold text-[#475569] dark:text-white/70 hover:bg-[#F1F5F9] dark:hover:bg-white/[0.06] transition-colors"
                data-testid="book-demo-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#0F62FE] text-white text-[14px] font-semibold hover:bg-[#0A4FCC] disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40 focus-visible:ring-offset-2 transition-colors"
                data-testid="book-demo-submit"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? 'Sending…' : 'Request a demo'}
              </button>
            </div>

            <p className="mt-4 text-[11px] text-[#94A3B8] dark:text-white/40 leading-relaxed">
              By submitting you agree to be contacted about your demo. Nova will not share your
              details with third parties.
            </p>
          </form>
        ) : (
          <div className="p-8 sm:p-10 text-center" data-testid="book-demo-success">
            <div className="w-12 h-12 rounded-full bg-[#ECFDF5] dark:bg-[#065F46]/40 border border-[#A7F3D0] dark:border-[#10B981]/40 inline-flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-[#10B981]" strokeWidth={2.25} />
            </div>
            <h3 className="text-[20px] font-bold text-[#0A0F1C] dark:text-[#F5F5F5]">
              Thanks — we&rsquo;ll be in touch.
            </h3>
            <p className="mt-2 text-[14px] text-[#475569] dark:text-white/70 leading-relaxed max-w-[40ch] mx-auto">
              A product specialist will reach out within one business day to schedule your
              walkthrough.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-[#0A0F1C] text-white text-[14px] font-semibold hover:bg-black dark:bg-white dark:text-[#0A0F1C] dark:hover:bg-white/90"
              data-testid="book-demo-success-close"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
